import React, { useRef } from 'react';

import { useNavigationTheme } from '@context/ThemeContext';
import { errorAlert } from '@helpers/Alert';
import { createNavigationAppError } from '@helpers/Errors';
import { getLinking } from '@helpers/Linking';
import { logError, logEvent } from '@helpers/Loggers';
import type { ParamListBase } from '@react-navigation/native';
import {
  NavigationContainer as RNNavigationContainer,
  useNavigationContainerRef,
} from '@react-navigation/native';

type NavigationContainerProps = React.ComponentProps<typeof RNNavigationContainer<ParamListBase>>;
type AppNavigationContainerProps = Omit<
  NavigationContainerProps,
  'documentTitle' | 'linking' | 'theme'
>;
type UnhandledNavigationAction = Parameters<
  NonNullable<NavigationContainerProps['onUnhandledAction']>
>[0];
const APP_DOCUMENT_TITLE: NonNullable<NavigationContainerProps['documentTitle']> = {
  formatter: () => 'Joinstar',
};

type NavigationActionLogPayload = {
  hasParams: boolean;
  nestedActionTypes: string[];
  routeNames: string[];
  source?: string;
  target?: string;
  type: string;
};

export function AppNavigationContainer({
  onReady,
  onStateChange,
  onUnhandledAction,
  ...props
}: AppNavigationContainerProps) {
  const linking = React.useMemo(() => getLinking(), []);
  const navigationTheme = useNavigationTheme();
  const navigationRef = useNavigationContainerRef<ParamListBase>();
  const routeNameRef = useRef('');

  return (
    <RNNavigationContainer<ParamListBase>
      {...props}
      documentTitle={APP_DOCUMENT_TITLE}
      linking={linking}
      ref={navigationRef}
      theme={navigationTheme}
      onReady={() => {
        routeNameRef.current = navigationRef.getCurrentRoute()?.name || '';
        onReady?.();
      }}
      onStateChange={(state) => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef.getCurrentRoute()?.name || '';

        if (previousRouteName !== currentRouteName) {
          // Log when screen changes
          logEvent('screen_view', { screen_name: currentRouteName });
        }

        // Update current route name
        routeNameRef.current = currentRouteName;
        onStateChange?.(state);
      }}
      onUnhandledAction={(action) => {
        handleUnhandledNavigationAction(action);
        onUnhandledAction?.(action);
      }}
    />
  );
}

export function handleUnhandledNavigationAction(action: UnhandledNavigationAction) {
  const error = new Error(`Unhandled navigation action: ${action.type}`);

  logError(error, {
    navigationAction: getNavigationActionLogPayload(action),
  });
  errorAlert(createNavigationAppError(error));
}

function getNavigationActionLogPayload(
  action: UnhandledNavigationAction
): NavigationActionLogPayload {
  const routeNames = new Set<string>();
  const nestedActionTypes = new Set<string>();

  collectActionMetadata(action, routeNames, nestedActionTypes);

  return {
    hasParams: hasNavigationParams(action),
    nestedActionTypes: [...nestedActionTypes],
    routeNames: [...routeNames],
    source: action.source,
    target: action.target,
    type: action.type,
  };
}

function collectActionMetadata(
  action: UnhandledNavigationAction,
  routeNames: Set<string>,
  nestedActionTypes: Set<string>
) {
  if (!isRecord(action.payload)) return;

  addString(routeNames, action.payload.name);
  addString(routeNames, action.payload.screen);
  collectNestedRouteNames(action.payload.params, routeNames);
  collectNestedRouteNames(action.payload.state, routeNames);

  if (isNavigationAction(action.payload.action)) {
    nestedActionTypes.add(action.payload.action.type);
    collectActionMetadata(action.payload.action, routeNames, nestedActionTypes);
  }
}

function collectNestedRouteNames(value: unknown, routeNames: Set<string>, depth = 0) {
  if (!isRecord(value) || depth > 5) return;

  addString(routeNames, value.screen);
  addString(routeNames, value.name);
  collectNestedRouteNames(value.params, routeNames, depth + 1);
  collectNestedRouteNames(value.state, routeNames, depth + 1);

  if (Array.isArray(value.routes)) {
    value.routes.forEach((route) => collectNestedRouteNames(route, routeNames, depth + 1));
  }
}

function hasNavigationParams(action: UnhandledNavigationAction): boolean {
  if (!isRecord(action.payload)) return false;

  if ('params' in action.payload && action.payload.params !== undefined) {
    return true;
  }

  return isNavigationAction(action.payload.action)
    ? hasNavigationParams(action.payload.action)
    : false;
}

function addString(values: Set<string>, value: unknown) {
  if (typeof value === 'string') {
    values.add(value);
  }
}

function isNavigationAction(value: unknown): value is UnhandledNavigationAction {
  return isRecord(value) && typeof value.type === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
